import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Form, Input, Select, Space, message, Popconfirm, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';

const { Option } = Select;

const CustomFieldsManager = ({ visible, onClose, onFieldsChanged }) => {
    const [fields, setFields] = useState([]);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fieldType, setFieldType] = useState('text');

    useEffect(() => {
        if (visible) fetchFields();
    }, [visible]);

    const fetchFields = async () => {
        try {
            const res = await axiosInstance.get('/custom-fields');
            setFields(res.data.custom_fields || []);
        } catch (e) {
            message.error('Failed to load custom fields.');
        }
    };

    const handleCreate = async (values) => {
        setLoading(true);
        try {
            const payload = {
                ...values,
                options: values.type === 'select'
                    ? values.options?.split(',').map(o => o.trim()).filter(Boolean)
                    : undefined,
            };
            const res = await axiosInstance.post('/custom-fields', payload);

            if (res.data.confirm_required) {
                Modal.confirm({
                    title: 'Share to connected shops?',
                    content: res.data.message,
                    okText: 'Yes, add to all',
                    cancelText: 'No, this shop only',
                    onOk: async () => {
                        await axiosInstance.post('/custom-fields', { ...payload, confirm_shared: true });
                        message.success('Custom field added and shared.');
                        fetchFields();
                        onFieldsChanged?.();
                        form.resetFields();
                    },
                    onCancel: async () => {
                        await axiosInstance.post('/custom-fields', { ...payload, confirm_shared: false });
                        message.success('Custom field added to this shop.');
                        fetchFields();
                        onFieldsChanged?.();
                        form.resetFields();
                    },
                });
                return;
            }

            message.success('Custom field added.');
            fetchFields();
            onFieldsChanged?.();
            form.resetFields();
        } catch (e) {
            message.error(e.response?.data?.errors?.name?.[0] || 'Failed to create field.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await axiosInstance.delete(`/custom-fields/${id}`);
            message.success('Field deleted.');
            fetchFields();
            onFieldsChanged?.();
        } catch (e) {
            message.error('Failed to delete field.');
        }
    };

    return (
        <Modal
            title="Manage Product Custom Fields"
            open={visible}
            onCancel={onClose}
            footer={null}
            width={700}
        >
            <Form form={form} layout="inline" onFinish={handleCreate} style={{ marginBottom: 16 }}>
                <Form.Item name="name" rules={[{ required: true, message: 'Name required' }]}>
                    <Input placeholder="Field name e.g. Color" />
                </Form.Item>
                <Form.Item name="type" initialValue="text" rules={[{ required: true }]}>
                    <Select style={{ width: 120 }} onChange={setFieldType}>
                        <Option value="text">Text</Option>
                        <Option value="number">Number</Option>
                        <Option value="date">Date</Option>
                        <Option value="select">Select</Option>
                    </Select>
                </Form.Item>
                {fieldType === 'select' && (
                    <Form.Item name="options" rules={[{ required: true, message: 'Comma-separated options' }]}>
                        <Input placeholder="Red, Blue, Green" />
                    </Form.Item>
                )}
                <Form.Item>
                    <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>
                        Add Field
                    </Button>
                </Form.Item>
            </Form>

            <Table
                dataSource={fields}
                rowKey="id"
                pagination={false}
                columns={[
                    { title: 'Name', dataIndex: 'name' },
                    { title: 'Type', dataIndex: 'type', render: t => <Tag>{t}</Tag> },
                    {
                        title: 'Options',
                        dataIndex: 'options',
                        render: (opts) => opts?.length ? opts.join(', ') : '-'
                    },
                    {
                        title: '',
                        render: (_, record) => (
                            <Popconfirm title="Delete this field?" onConfirm={() => handleDelete(record.id)}>
                                <Button danger icon={<DeleteOutlined />} size="small" />
                            </Popconfirm>
                        )
                    },
                ]}
            />
        </Modal>
    );
};

export default CustomFieldsManager;